import {
  DataSyncIcon,
  KeyboardArrowRight,
  ListViewIcon,
} from '@/components/icons'
import { useAuthContext } from '@/features/auth/context'
import { loadGuidedSetupProgress } from '../../SetUpGuided/guided-setup-progress'
import { CONFIG_SETUP_ROUTES } from '@/features/design-system/routes'
import useMk2ImportSettling from '@/features/config/hooks/controller/useMk2ImportSettling'
import { OPERATIONS_ROUTES } from '@/constant/route'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { Box, Button, Stack, Typography } from '@mui/material'
import { memo, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'

type Props = {
  status: number
  commonDataStatus?: number
}

export const DONE_DISCOVER = 50
export const DONE_COMMON = 100

type CardState = 'done' | 'active' | 'locked'

const BRAND = '#004744'

interface SetupCardProps {
  step: number
  state: CardState
  icon: ReactNode
  title: string
  description: string
  status: string
  children: ReactNode
}

const SetupCard = ({
  step,
  state,
  icon,
  title,
  description,
  status,
  children,
}: SetupCardProps) => {
  const isDone = state === 'done'
  const isLocked = state === 'locked'

  return (
    <Stack
      sx={{
        width: 320,
        borderRadius: '16px',
        p: '24px',
        gap: '20px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'border.main',
        opacity: isLocked ? 0.6 : 1,
        transition:
          'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? 'none'
            : '0 1px 2px rgba(17,49,46,0.04)',
        '&:hover': isLocked
          ? undefined
          : {
              transform: 'translateY(-4px)',
              borderColor: 'primary.main',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 16px 34px rgba(0,0,0,0.45)'
                  : '0 16px 34px rgba(0,71,68,0.12)',
            },
      }}
    >
      {/* Step number left, one plain status line right — no pills, so the card
          has a single row of small type rather than competing badges. */}
      <Stack
        direction="row"
        sx={{ height: 18, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography
          sx={{
            color: 'grey.600',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.12em',
          }}
        >
          STEP {step}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', gap: '4px' }}>
          {isDone && (
            <CheckRoundedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
          )}
          <Typography
            sx={{
              color: isDone ? 'primary.main' : 'grey.600',
              fontSize: '12px',
              fontWeight: isDone ? 600 : 400,
            }}
          >
            {status}
          </Typography>
        </Stack>
      </Stack>

      <Stack sx={{ gap: '12px' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(150deg, rgba(255,255,255,0.12), rgba(255,255,255,0.05))'
                : 'linear-gradient(150deg, rgba(0,71,68,0.11), rgba(0,71,68,0.05))',
            '& svg': { width: 22, height: 22 },
          }}
        >
          {icon}
        </Box>
        <Typography
          sx={{ color: 'text.main', fontSize: '18px', fontWeight: 600 }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ color: 'grey.600', fontSize: '13px', lineHeight: 1.55 }}
        >
          {description}
        </Typography>
      </Stack>

      <Stack sx={{ gap: '8px', mt: 'auto' }}>{children}</Stack>
    </Stack>
  )
}

const buttonSx = {
  width: '100%',
  height: 40,
  fontSize: '13px',
  fontWeight: 500,
  borderRadius: '10px',
  textTransform: 'none' as const,
}

const primarySx = {
  ...buttonSx,
  color: '#fff',
  background: `linear-gradient(120deg, ${BRAND} 0%, #0A6B62 100%)`,
  boxShadow: '0 6px 16px rgba(0,71,68,0.22)',
  transition: 'box-shadow 200ms ease, transform 200ms ease',
  '&:hover': {
    background: `linear-gradient(120deg, ${BRAND} 0%, #0A6B62 100%)`,
    boxShadow: '0 10px 22px rgba(0,71,68,0.30)',
    transform: 'translateY(-1px)',
  },
}

// Second route into the data, not a footnote: a real button, kept lighter than
// the primary so the guided path still reads as the default.
const secondarySx = {
  ...buttonSx,
  color: 'text.main',
  border: '1px solid',
  borderColor: 'border.main',
  backgroundColor: 'transparent',
  '&:hover': {
    borderColor: 'primary.main',
    color: 'primary.main',
    backgroundColor: (theme: any) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(0,71,68,0.05)',
  },
}

/**
 * SCRUM-1032 — Getting Started cards.
 *
 * Discover Data then Common Data. One status line and one primary action per
 * card; Common Data's second route into the data (the Excel import) sits under
 * its primary as a quiet link rather than a second solid button.
 */
const BoxWrapper = memo(({ status, commonDataStatus }: Props) => {
  const { id } = useParams()
  const { interfaceId } = useAuthContext()
  const setupId = id || interfaceId + '' || ''
  const guidedProgress = loadGuidedSetupProgress(Number(setupId))
  // An MK2 import is still landing (and may be followed by its own resync). A
  // discovery now deletes every decoder the controller does not report back,
  // which can be the ones just imported — so it waits.
  const isImportSettling = useMk2ImportSettling(Number(setupId))

  const discoverDone = status >= DONE_DISCOVER
  const commonDone = status >= DONE_COMMON
  const commonProgress = commonDone ? 100 : (commonDataStatus ?? 0)

  return (
    <Stack sx={{ alignItems: 'center', gap: '20px', mt: '40px', width: '100%' }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: '20px',
          flexWrap: 'wrap',
        }}
      >
        <SetupCard
          step={1}
          state={discoverDone ? 'done' : 'active'}
          icon={<DataSyncIcon />}
          title="Discover Decoders"
          description="Find the decoders, line units and sensors installed on this controller."
          status={discoverDone ? 'Completed' : 'Not started'}
        >
          <Button
            variant={discoverDone ? 'secondary' : 'primary'}
            component={Link}
            to={CONFIG_SETUP_ROUTES.discovery.to(setupId)}
            sx={discoverDone ? buttonSx : primarySx}
            disabled={isImportSettling}
            data-testid="toolbar-discover"
          >
            {discoverDone ? 'Rediscover Decoders' : 'Discover Decoders'}
          </Button>
        </SetupCard>

        <SetupCard
          step={2}
          state={commonDone ? 'done' : discoverDone ? 'active' : 'locked'}
          icon={<ListViewIcon />}
          title="Common Data"
          description="Pipes & pumps, plants, soil, areas and stations — the data your schedules run on."
          status={
            commonDone
              ? 'Completed'
              : discoverDone
                ? `${commonProgress}% complete`
                : 'Locked'
          }
        >
          {discoverDone ? (
            <>
              <Button
                variant="primary"
                component={Link}
                to={CONFIG_SETUP_ROUTES.setupSecondGuided.to(setupId)}
                sx={primarySx}
              >
                {commonDone
                  ? 'Review common data'
                  : guidedProgress?.status === 'in_progress'
                    ? 'Continue setup in app'
                    : 'Set up in app'}
              </Button>
              <Button
                component={Link}
                to={CONFIG_SETUP_ROUTES.setupSecond.to(setupId)}
                sx={secondarySx}
              >
                Import from Excel
              </Button>
            </>
          ) : (
            <Typography
              sx={{
                color: 'grey.600',
                fontSize: '12px',
                textAlign: 'center',
                py: '11px',
              }}
            >
              Unlocks once discovery is done
            </Typography>
          )}
        </SetupCard>
      </Stack>

      {commonDone && (
        <Button
          component={Link}
          to={OPERATIONS_ROUTES.index.to}
          sx={{
            width: 660,
            maxWidth: '100%',
            height: 56,
            borderRadius: '14px',
            justifyContent: 'space-between',
            px: '24px',
            textTransform: 'none',
            color: '#fff',
            backgroundColor: BRAND,
            '&:hover': { backgroundColor: '#0A6B62' },
          }}
        >
          <Stack sx={{ alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              Setup complete
            </Typography>
            <Typography sx={{ fontSize: '12px', opacity: 0.8 }}>
              Head to the dashboard and start irrigating
            </Typography>
          </Stack>
          <KeyboardArrowRight style={{ width: 24, height: 24 }} />
        </Button>
      )}
    </Stack>
  )
})

export default BoxWrapper
